import { create } from "zustand";
import { CreditInformationResponse, UserConfig } from "../api/types";
import { getCreditInformation, getUserCards, getUserConfig } from "../api/api";
import { useSDKConfigStore } from "./sdkConfigStore";
import {
  BASE_CHAIN_ID,
  getAllCreditProviders,
  getCreditProvider,
  mapStoreKeyToApiType,
  StoreCreditType,
} from "../config/creditRegistry";

// Generate store record type dynamically
type CreditInfoRecord = Record<
  StoreCreditType,
  CreditInformationResponse | null
>;

const getInitialCreditInfo = (): CreditInfoRecord => {
  const allProviders = getAllCreditProviders();
  return allProviders.reduce((acc, provider) => {
    acc[provider.storeKey as StoreCreditType] = null;
    return acc;
  }, {} as CreditInfoRecord);
};

interface CreditState {
  creditInfo: CreditInfoRecord;
  userConfig: UserConfig["config"] | null;
  isLoading: boolean;
  isRefreshingPositions: boolean;
  error: string | null;
  // Internal methods for SDK use only
  _setUserConfig: (config: Record<string, string> | null) => void;
  _setCreditInfo: (
    type: StoreCreditType,
    info: CreditInformationResponse
  ) => void;
  _setLoading: (loading: boolean) => void;
  _setError: (error: string | null) => void;
  _reset: () => void;
  // New function to refresh positions
  _refreshPositions: (creditTypes: StoreCreditType[]) => Promise<void>;
}

export const useCreditStore = create<CreditState>((set, get) => ({
  creditInfo: {} as CreditInfoRecord, // Will be initialized dynamically
  isLoading: false,
  isRefreshingPositions: false,
  error: null,
  userConfig: null,

  // Internal methods for SDK use only (prefixed with _)
  _setCreditInfo: (type, info) =>
    set((state) => ({
      creditInfo: {
        ...state.creditInfo,
        [type]: info,
      },
    })),

  _setUserConfig: (config) => set({ userConfig: config }),

  _setLoading: (loading) => set({ isLoading: loading }),
  _setError: (error) => set({ error }),

  _reset: () =>
    set({
      creditInfo: getInitialCreditInfo(), // Dynamic reset
      isLoading: false,
      error: null,
    }),

  // New function to refresh positions for specified credit types
  _refreshPositions: async (creditTypes: StoreCreditType[]) => {
    const { jwt, isInitialized } = useSDKConfigStore.getState();

    if (!isInitialized || !jwt?.eoa) {
      console.error(
        "❌ SDK: Cannot refresh positions - SDK not initialized or no EOA address"
      );
      return;
    }

    console.log("🔄 SDK: Refreshing positions for:", creditTypes);

    // Set loading state
    set({ error: null, isRefreshingPositions: true });

    try {
      const userCardsResponse = await getUserCards();

      // card data
      const userCard = userCardsResponse.data?.[0] || null;

      const userConfig = userCard
        ? {
            solverType: userCard.solverType,
            config: userCard.solverConfig,
          }
        : await getUserConfig();

      const fetchPromises = creditTypes.map((storeKey) => {
        const creditType = mapStoreKeyToApiType(storeKey);

        if (!creditType) {
          throw new Error(`Unknown store key: ${storeKey}`);
        }

        const provider = getCreditProvider(creditType);

        const currentConfig =
          userConfig?.solverType === provider?.id
            ? userConfig?.config
            : undefined;

        return getCreditInformation(
          creditType,
          provider?.chainId ?? BASE_CHAIN_ID,
          currentConfig
        );
      });

      const responses = await Promise.all(fetchPromises);

      // Update store with fetched data
      responses.forEach((response, index) => {
        const storeKey = creditTypes[index] as StoreCreditType;
        get()._setCreditInfo(storeKey, response);
      });

      console.log(
        "✅ SDK: Successfully refreshed positions for:",
        creditTypes.join(", ")
      );
    } catch (error) {
      console.error("❌ SDK: Error refreshing positions:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to refresh positions";
      set({ error: errorMessage });
    } finally {
      set({ isRefreshingPositions: false });
    }
  },
}));
